import * as React from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

/** Limit the number of toasts shown at once */
const TOAST_LIMIT = 1;
// Removed TOAST_REMOVE_DELAY as the underlying component likely handles duration

/** Represents the properties of a toast internally, including its ID. */
type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

/** Action types for the toast reducer. */
const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  // REMOVE_TOAST: "REMOVE_TOAST", // Removed this action
} as const

/** Counter for generating unique toast IDs. */
let count = 0

/** Generates a unique ID for each toast. */
function genId(): string {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

/** Type definition for the possible actions. */
type ActionType = typeof actionTypes

/** Union type for all possible reducer actions. */
type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  // Removed REMOVE_TOAST action type

/** Interface defining the shape of the toast state. */
interface State {
  toasts: ToasterToast[]
}

// Removed toastTimeouts Map and addToRemoveQueue function

/**
 * Reducer function to manage the toast state.
 * @param state - The current state.
 * @param action - The dispatched action.
 * @returns The new state.
 */
export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      return {
        ...state,
        // Add new toast and slice to enforce limit
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case actionTypes.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action

      // If toastId is provided, dismiss that specific toast.
      // If not, dismiss all toasts.
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false, // Set open to false to trigger close animation/logic
              }
            : t
        ),
      }
    }
    // Removed REMOVE_TOAST case
    // Default case to handle potential unknown actions, though TS should prevent this
    default:
       return state;
  }
}

/** Array of listener functions subscribed to state changes. */
const listeners: Array<(state: State) => void> = []

/** The global state object. */
let memoryState: State = { toasts: [] }

/**
 * Dispatches an action to update the toast state and notifies listeners.
 * @param action - The action to dispatch.
 */
function dispatch(action: Action): void {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

/** Type for the props passed to the public `toast` function (omits `id`). */
type Toast = Omit<ToasterToast, "id">

/**
 * Public function to add a new toast.
 * @param props - The properties of the toast (title, description, variant, etc.).
 * @returns An object with the new toast's ID and methods to update/dismiss it.
 */
function toast({ ...props }: Toast) {
  const id = genId()

  /** Updates the properties of this specific toast. */
  const update = (props: ToasterToast) =>
    dispatch({
      type: actionTypes.UPDATE_TOAST,
      toast: { ...props, id },
    })

  /** Dismisses this specific toast. */
  const dismiss = () => dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id })

  // Dispatch ADD_TOAST action with the new toast details
  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true, // Ensure toast starts open
      // Setup callback for when the underlying component requests closing
      onOpenChange: (open) => {
        if (!open) dismiss() // If component closes, dispatch dismiss action
      },
    },
  })

  // Return control object for the created toast
  return {
    id: id,
    dismiss,
    update,
  }
}

/**
 * React hook to access the toast state and dispatcher functions.
 * @returns An object containing the current toasts, the `toast` function to add new toasts,
 * and the `dismiss` function to dismiss toasts.
 */
function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    // Subscribe to state changes
    listeners.push(setState)
    // Unsubscribe on component unmount
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state]) // Re-subscribe if state identity changes (shouldn't normally happen)

  return {
    ...state, // Current toasts ({ toasts: ToasterToast[] })
    toast, // Function to add a new toast: (props: Toast) => { id, update, dismiss }
    // Function to dismiss a toast by ID, or all toasts if no ID is given
    dismiss: (toastId?: string) => dispatch({ type: actionTypes.DISMISS_TOAST, toastId }),
  }
}

export { useToast, toast }
